import React from 'react';
import { getDeviseCourante } from '../../utils/format';

// BonCommandeA4 — rendu du bon au format A4 portrait.
//
// Utilisable :
//   1. Inline dans un modal de prévisualisation (avec son propre CSS)
//   2. Cible de window.print() (la feuille @media print en bas masque tout
//      ce qui n'est pas le bon)
//   3. Source de html2canvas → jsPDF pour le téléchargement PDF
//
// Toutes les couleurs sont en HEX (pas de variables CSS) pour que ça
// reste correct quel que soit le contexte de rendu (preview, print, PDF).

function fmtGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}
function fmtDateFR(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

export default function BonCommandeA4({ bon, parametres = {}, logoUrl = '/logoSV.png' }) {
  if (!bon) return null;

  const nomEntreprise = parametres.nom_entreprise || 'Mon entreprise';
  const adresse       = parametres.adresse || '';
  const ville         = parametres.ville || 'Conakry, Guinée';
  const telephoneEnt  = parametres.telephone || '';
  const emailEnt      = parametres.email || '';

  const lignes = bon.lignes || [];
  const nbArticles = lignes.reduce((s, l) => s + Number(l.quantite || 0), 0);

  return (
    <>
      <div
        id="bon-commande-print"
        style={{
          background: '#fff',
          color: '#111',
          padding: '24px 28px',
          maxWidth: '780px',
          margin: '0 auto',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          fontSize: '11px',
          lineHeight: 1.4,
          boxSizing: 'border-box',
        }}
      >
        {/* En-tête : identité + titre */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: '16px',
          paddingBottom: '14px', borderBottom: '2px solid #111',
        }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <img
              src={logoUrl} alt="logo"
              style={{
                width: '56px', height: '56px', objectFit: 'contain',
                border: '0.5px solid #ddd', borderRadius: '4px', padding: '2px',
              }}
            />
            <div>
              <p style={{ fontWeight: 600, fontSize: '14px', margin: '0 0 2px' }}>{nomEntreprise}</p>
              {adresse && <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>{adresse}</p>}
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>{ville}</p>
              {telephoneEnt && <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Tél : {telephoneEnt}</p>}
              {emailEnt && <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Email : {emailEnt}</p>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontWeight: 600, fontSize: '18px', margin: '0 0 6px', letterSpacing: '1px' }}>
              BON DE COMMANDE
            </p>
            <table style={{ borderCollapse: 'collapse', marginLeft: 'auto', fontSize: '10px' }}>
              <tbody>
                <tr><td style={{ padding: '1px 8px 1px 0', color: '#555' }}>N°</td>
                    <td style={{ padding: '1px 0', fontWeight: 600 }}>{bon.numero}</td></tr>
                <tr><td style={{ padding: '1px 8px 1px 0', color: '#555' }}>Émis le</td>
                    <td style={{ padding: '1px 0' }}>{fmtDateFR(bon.date_emission)}</td></tr>
                <tr><td style={{ padding: '1px 8px 1px 0', color: '#555' }}>Livraison</td>
                    <td style={{ padding: '1px 0' }}>{bon.date_livraison_prevue ? fmtDateFR(bon.date_livraison_prevue) : 'À convenir'}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Blocs fournisseur + émetteur */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '12px', margin: '16px 0',
        }}>
          <div style={{ border: '0.5px solid #ccc', borderRadius: '4px', padding: '10px 12px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Fournisseur
            </p>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '12px' }}>{bon.fournisseur_nom}</p>
            {bon.fournisseur_specialite && (
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Spécialité : {bon.fournisseur_specialite}</p>
            )}
            {bon.fournisseur_adresse && (
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>{bon.fournisseur_adresse}</p>
            )}
            {bon.fournisseur_telephone && (
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Tél : {bon.fournisseur_telephone}</p>
            )}
          </div>
          <div style={{ border: '0.5px solid #ccc', borderRadius: '4px', padding: '10px 12px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Commande émise par
            </p>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '12px' }}>{bon.emetteur_nom}</p>
            {bon.emetteur_role && (
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Rôle : {bon.emetteur_role}</p>
            )}
            <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Devise : {getDeviseCourante()}</p>
            <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Mode de règlement : à préciser</p>
          </div>
        </div>

        {/* Tableau articles */}
        <table style={{
          width: '100%', borderCollapse: 'collapse',
          fontSize: '10.5px', margin: '8px 0', tableLayout: 'fixed',
        }}>
          <colgroup>
            <col style={{ width: '26px' }} />
            <col />
            <col style={{ width: '80px' }} />
            <col style={{ width: '42px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '92px' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f5f5f5', borderTop: '1px solid #111', borderBottom: '1px solid #111' }}>
              <th style={thS('center')}>#</th>
              <th style={thS('left')}>Désignation</th>
              <th style={thS('left')}>Réf.</th>
              <th style={thS('center')}>Qté</th>
              <th style={thS('right')}>P.U. ({getDeviseCourante()})</th>
              <th style={thS('right')}>Total ({getDeviseCourante()})</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => {
              const qte = Number(l.quantite || 0);
              const pu  = Number(l.prix_achat_unitaire || 0);
              const st  = Number(l.sous_total || qte * pu);
              const nom = [l.produit_nom, l.modele, l.taille_nom, l.couleur && `(${l.couleur})`]
                .filter(Boolean).join(' ');
              return (
                <tr key={l.id || i} style={{ borderBottom: '0.5px solid #eee', verticalAlign: 'top' }}>
                  <td style={tdS('center', '#888')}>{i + 1}</td>
                  <td style={{ ...tdS('left'), wordWrap: 'break-word', overflowWrap: 'break-word' }}>{nom}</td>
                  <td style={{ ...tdS('left', '#555'), fontFamily: 'monospace', fontSize: '10px' }}>{l.code_variante || '—'}</td>
                  <td style={tdS('center')}>{qte}</td>
                  <td style={tdSNum('right')}>{pu.toLocaleString('fr-FR')}</td>
                  <td style={{ ...tdSNum('right'), fontWeight: 600 }}>{st.toLocaleString('fr-FR')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totaux à droite */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '11px', minWidth: '240px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '4px 12px 4px 0', color: '#555' }}>Nombre d'articles</td>
                <td style={{ padding: '4px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {nbArticles} pièces
                </td>
              </tr>
              <tr>
                <td style={{ padding: '4px 12px 4px 0', color: '#555' }}>Sous-total</td>
                <td style={{ padding: '4px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtGNF(bon.cout_total)}
                </td>
              </tr>
              <tr style={{ borderTop: '2px solid #111' }}>
                <td style={{ padding: '8px 12px 8px 0', fontWeight: 600, fontSize: '13px' }}>TOTAL À PAYER</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600, fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtGNF(bon.cout_total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Notes / conditions */}
        <div style={{
          marginTop: '16px', border: '0.5px solid #ccc', borderRadius: '4px',
          padding: '10px 12px', minHeight: '40px',
        }}>
          <p style={{ margin: '0 0 4px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Notes / conditions
          </p>
          <p style={{ margin: 0, fontSize: '10px', color: '#555', fontStyle: bon.notes ? 'normal' : 'italic' }}>
            {bon.notes || 'Livraison à confirmer. Marchandise contrôlée à l\'arrivée.'}
          </p>
        </div>

        {/* Signatures */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '30px', marginTop: '28px', paddingTop: '16px',
        }}>
          <div>
            <p style={{ margin: '0 0 36px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Pour {nomEntreprise}
            </p>
            <div style={{ borderTop: '0.5px solid #111', paddingTop: '4px' }}>
              <p style={{ margin: 0, fontSize: '10px', color: '#555' }}>Nom · Date · Signature</p>
            </div>
          </div>
          <div>
            <p style={{ margin: '0 0 36px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Bon pour accord — Fournisseur
            </p>
            <div style={{ borderTop: '0.5px solid #111', paddingTop: '4px' }}>
              <p style={{ margin: 0, fontSize: '10px', color: '#555' }}>Nom · Date · Signature &amp; cachet</p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '18px', textAlign: 'center', fontSize: '9px', color: '#999' }}>
          {nomEntreprise} · Document généré le {fmtDateFR(new Date())} · Réf {bon.numero}
        </div>
      </div>

      {/* Feuille d'impression : ne montre QUE le bon, masque le reste. */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body * { visibility: hidden; }
          #bon-commande-print, #bon-commande-print * { visibility: visible; }
          #bon-commande-print {
            position: fixed; top: 0; left: 0;
            width: 100%; max-width: none;
            padding: 0;
          }
        }
      `}</style>
    </>
  );
}

// Helpers de style pour éviter la répétition
function thS(align) {
  return {
    textAlign: align, padding: '6px 4px',
    fontSize: '9.5px', fontWeight: 600, textTransform: 'uppercase',
  };
}
function tdS(align, color = '#111') {
  return { textAlign: align, padding: '6px 4px', color };
}
function tdSNum(align) {
  return { ...tdS(align), whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
}
